import React from "react";
import {
  Recycle,
  ShieldCheck,
  Network,
  Lightbulb,
  Sprout,
  Building2,
  MapPin,
  Truck,
  Clock,
  Landmark,
} from "lucide-react";

const FEATURES = [
  {
    icon: Recycle,
    title: "Circular Energy Systems",
    desc: "Designed for long-term efficiency with reduced waste and optimized resource cycles.",
    accent: "#138808",
  },
  {
    icon: ShieldCheck,
    title: "Industrial Reliability",
    desc: "Built for consistent uptime and stable supply across high-demand operations.",
    accent: "#FF9933",
  },
  {
    icon: Network,
    title: "Expanding National Network",
    desc: "Rapid infrastructure rollout across highways, cities, and industrial zones.",
    accent: "#4FB3FF",
  },
  {
    icon: Sprout,
    title: "Sustainable Growth Model",
    desc: "Every expansion strengthens India’s clean and self-reliant energy ecosystem.",
    accent: "#138808",
  },
];

const WHY_US = [
  {
    icon: Building2,
    title: "Mid-Market Infrastructure Layer",
    desc: "We sit at the core of India’s energy growth — bridging large-scale supply with local demand networks.",
    accent: "#FF9933",
  },
  {
    icon: MapPin,
    title: "Local Franchise Ecosystem",
    desc: "We create strong on-ground partnerships that generate local ownership and long-term business value.",
    accent: "#138808",
  },
  {
    icon: Truck,
    title: "Faster Supply & Distribution",
    desc: "Optimized logistics ensure quicker delivery cycles and uninterrupted fuel availability.",
    accent: "#4FB3FF",
  },
  {
    icon: Clock,
    title: "On-Time Infrastructure Delivery",
    desc: "We follow strict execution timelines and deliver projects exactly as committed.",
    accent: "#FF9933",
  },
  {
    icon: Landmark,
    title: "Government Alignment & Compliance",
    desc: "We Respect the government and strictly follow all regulatory, safety, and energy policies.",
    accent: "#138808",
  },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why-choose-us"
      className="relative bg-[#070a0f] text-white overflow-hidden py-28 md:py-36"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-[3px] bg-[#FF9933]" />
        <div className="absolute top-0 left-0 w-1/3 h-full bg-[#FF9933]/5 blur-3xl" />
        <div className="absolute top-0 left-1/3 w-1/3 h-full bg-white/5 blur-3xl" />
        <div className="absolute top-0 right-0 w-1/3 h-full bg-[#138808]/5 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-[#138808]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">

        <div className="max-w-5xl">
          <span className="text-white/50 text-xs tracking-[0.3em] uppercase">
            Why INDIANECOFUELS ?
          </span>

          <h2 className="mt-4 text-5xl md:text-6xl font-extrabold leading-tight">
            <span className="block text-[#FF9933]">Built For India’s</span>
            <span className="block text-white">Energy And Future</span>
            <span className="block text-[#138808]">with Real-World Execution</span>
          </h2>

          <p className="mt-6 text-white/60 text-lg leading-relaxed">
            We are building a national energy infrastructure ecosystem that connects
            production, distribution, and local partnerships — designed for scale,
            compliance, and long-term reliability.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 flex flex-col justify-between"
            >
              <div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: `${f.accent}22`,
                    border: `1px solid ${f.accent}55`,
                  }}
                >
                  <f.icon className="w-5 h-5" style={{ color: f.accent }} />
                </div>

                <h3 className="text-lg font-semibold">{f.title}</h3>
                <p className="text-white/60 text-sm mt-2 leading-relaxed">
                  {f.desc}
                </p>
              </div>

              <div className="text-xs text-white/30 mt-6 tracking-widest">
                0{i + 1}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24">
          <h3 className="text-3xl md:text-4xl font-bold">
            Why the market is moving toward us :
          </h3>

          <p className="mt-4 text-white/60 max-w-3xl">
            Growth in India’s energy sector is driven by execution, trust, and
            infrastructure reliability — not promises.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr">
            {WHY_US.map((w, i) => (
              <div
                key={i}
                className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 flex gap-4"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${w.accent}22`,
                    border: `1px solid ${w.accent}55`,
                  }}
                >
                  <w.icon className="w-5 h-5" style={{ color: w.accent }} />
                </div>

                <div>
                  <h4 className="font-semibold">{w.title}</h4>
                  <p className="text-white/60 text-sm mt-1 leading-relaxed">
                    {w.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-28 text-center max-w-3xl mx-auto">
          <div className="h-[2px] w-24 mx-auto bg-gradient-to-r from-[#FF9933] via-white to-[#138808] mb-8" />

          <h3 className="text-3xl md:text-5xl font-bold">
            Built in India. Built for scale.
          </h3>

          <p className="mt-5 text-white/60 leading-relaxed">
            We are not just expanding infrastructure — we are building a compliant,
            government-aligned, future-ready energy backbone for the country.
          </p>
        </div>

      </div>
    </section>
  );
}